import React, { useState, useEffect } from 'react';

// Sagas
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';

import {
  Alert, Button, Modal, ModalBody, ModalFooter, ModalHeader,
} from 'reactstrap';

import toastr from 'toastr';
import 'toastr/build/toastr.min.css';
import moment from 'moment';
import { get } from '../../helpers/api_helper';
import BaseGrid from '../Common/BaseGrid';

function ChannelAdvisor(props) {
  // eslint-disable-next-line no-unused-vars
  const { t, listing, channel } = props;

  const [productInfo, setProductInfo] = useState(null);
  const [, setLoading] = useState(false);
  const [pushing, setPushing] = useState(false);
  const [channelLog, setChannelLog] = useState(null);
  const [remoteId, setRemoteId] = useState(props.remoteId);
  const [modalOpen, setModalOpen] = useState(false);

  toastr.options = {
    positionClass: 'toast-top-right',
    newestOnTop: true,
  };

  const fetchData = async () => {
    setLoading(true);

    try {
      if (remoteId !== null) {
        const pinfo = await get(`/api/ca/product/${remoteId}`);
        const log = await get(`/api/ca/channel-logs?f=remote_id=${remoteId}&page=1&sort=id&dir=desc&per=30&`);

        setProductInfo(pinfo);

        if (log.results.length > 0) {
          setChannelLog(log.results[0]);
        }
      }
    } catch (e) {
      toastr.error(e.message);
      if (e.response && e.response.data && e.response.data.error) {
        toastr.error(e.response.data.error);
      }
    } finally {
      setLoading(false);
    }
  };

  const pushSync = async () => {
    setPushing(true);

    try {
      await get(`/api/ca/push/${remoteId}`);

      fetchData();
    } catch (e) {
      toastr.error(e.message);
      if (e.response && e.response.data && e.response.data.error) {
        toastr.error(e.response.data.error);
      }
    } finally {
      setPushing(false);
    }
  };

  const createRemote = async () => {
    setPushing(true);

    try {
      const response = await get(`/api/ca/create/${listing.id}`);

      setRemoteId(response.remote_id);

      fetchData();
    } catch (e) {
      toastr.error(e.message);
      if (e.response && e.response.data && e.response.data.error) {
        toastr.error(e.response.data.error);
      }
    } finally {
      setPushing(false);
    }
  };

  useEffect(() => {
    if (remoteId !== null) {
      fetchData();
    }
  }, [remoteId]);

  const jsx = (
    <div>
      {channel.deletion_requested_at && (
        <Alert color={channel.deletion_error ? 'danger' : 'warning'}>
          <strong>{t(channel.deletion_error ? 'Rithum deletion failed' : 'Rithum deletion pending')}</strong>
          <div>
            {t(channel.deletion_labels_removed_at
              ? 'Rithum labels removed; waiting to delete the product.'
              : 'Removing Rithum labels before deleting the product.')}
          </div>
          {channel.deletion_attempted_at && (
            <div>{`${t('Last attempt')}: ${moment(channel.deletion_attempted_at).format('L LT')}`}</div>
          )}
          {channel.deletion_error && <div>{channel.deletion_error}</div>}
          <div>{t('The listing remains linked in Party until Rithum confirms deletion.')}</div>
        </Alert>
      )}

      {remoteId === null && (
        <Button
          color="success"
          className="text-dark"
          onClick={createRemote}
          disabled={pushing}
        >
          {pushing && (
            <span>{t('Creating...')}</span>
          )}

          {!pushing && (
            <span>{t('Create Remote')}</span>
          )}
        </Button>
      )}

      {remoteId !== null && productInfo !== null && (
        <>
          <h6 className="mb-3">{t('Remote Details')}</h6>

          {productInfo !== null && (
            <a
              href={`https://complete.channeladvisor.com/Inventory/ProductEdit.mvc/Edit?apid=12003175&id=${productInfo.ID}`}
              target="_blank"
              style={{ fontSize: '11px' }}
              rel="noreferrer"
            >
              <span className="mdi mdi-application-export" />
              &nbsp;
              {`https://complete.channeladvisor.com/Inventory/ProductEdit.mvc/Edit?apid=12003175&id=${productInfo.ID}`}
            </a>
          )}

          <table className="table table-bordered table-sm mt-3">
            <tbody>
              <tr>
                <th>{t('Product ID')}</th>
                <td>{productInfo.ID}</td>
              </tr>
              <tr>
                <th>{t('Data Last Changed')}</th>
                <td>{moment(productInfo.UpdateDateUtc).toLocaleString()}</td>
              </tr>
              <tr>
                <th>{t('Inventory Last Changed')}</th>
                <td>{moment(productInfo.QuantityUpdateDateUtc).toLocaleString()}</td>
              </tr>
              <tr>
                <th>{t('Total Available Qty')}</th>
                <td>{productInfo.TotalAvailableQuantity}</td>
              </tr>
            </tbody>
          </table>

          <span className="mdi mdi-information-outline" />
          &nbsp;
          {t('Last Update Attempt:')}
          &nbsp;
          {channelLog !== null ? new Date(channelLog.attempted_at).toLocaleString('en-US', {
            weekday: 'short', year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: 'numeric', second: 'numeric',
          }) : '?'}
          &nbsp;
        </>
      )}

      {remoteId !== null && (
        <div>
          <div className=" mt-3 hstack gap-2">
            <Button
              color="primary"
              onClick={pushSync}
              disabled={pushing}
            >
              {pushing && (
                <span>{t('Pushing...')}</span>
              )}

              {!pushing && (
                <span>{t('Push Sync')}</span>
              )}
            </Button>

            <Button color="info" title={t('View Logs')} onClick={() => { setModalOpen(true); }} className="text-dark btn-soft-info">
              <span className="mdi mdi-eye" />
              &nbsp;
              {t('View Logs')}
            </Button>

          </div>

          <Modal
            size="lg"
            isOpen={modalOpen}
          >
            <ModalHeader>
              <h5
                className="modal-title mt-0"
                id="myLargeModalLabel"
              >
                {t('Channel Logs')}
              </h5>
              <button
                onClick={() => setModalOpen(false)}
                type="button"
                className="close"
                data-dismiss="modal"
                aria-label="Close"
              >
                <span aria-hidden="true">&times;</span>
              </button>
            </ModalHeader>
            <ModalBody>
              <BaseGrid
                url="/api/ca/channel-logs"
                filters={`remote_id=${remoteId}`}
                config={
                {
                  columns: {
                    attempted_at: {
                      label: props.t('Attempted At'),
                      date: {
                        format: (date) => (new Date(date).toLocaleString()),
                      },
                    },
                    message: {
                      label: props.t('Message'),
                    },
                    result: {
                      label: props.t('Result'),
                      format: (v, x) => (x.inventory_synced_at || x.data_synced_at ? 'Success' : 'Failed'),
                    },
                  },
                  pagination: {
                    sort: 'id',
                    direction: 'desc',
                    perPage: 5,
                  },
                }
                }
              />
            </ModalBody>
            <ModalFooter>
              <button
                type="button"
                onClick={() => setModalOpen(false)}
                className="btn btn-soft-dark"
                data-dismiss="modal"
              >
                {t('Close')}
              </button>
            </ModalFooter>
          </Modal>
        </div>
      )}
    </div>
  );

  return jsx;
}

ChannelAdvisor.defaultProps = {
  t: (x) => (x),
  remoteId: null,
};

ChannelAdvisor.propTypes = {
  t: PropTypes.any,
  remoteId: PropTypes.any,
  listing: PropTypes.object.isRequired,
  channel: PropTypes.object.isRequired,
};

export default connect(() => ({

}), {})(withTranslation()(ChannelAdvisor));
